import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import type { TabsProps } from "@mui/material/Tabs";
import Tabs from "@mui/material/Tabs";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { ExternalLink } from "@/components/ExternalLink";
import { InfoBox } from "@/components/InfoBox";
import { Typography } from "@/components/Typography";
import { ProfileUnificationWarning } from "@/features/unify-scope-setting/ProfileUnificationWarning";
import { interlude } from "@/interlude";
import { ConnectCdhButton } from "@/pages/applications/components/CdhCard";
import { CdhSettings } from "@/pages/applications/pages/cdh";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";
import type { DataHubTab } from "@/pages/dataHub/dataHubSearchSchema";
import { DATA_HUB_TABS } from "@/pages/dataHub/dataHubSearchSchema";
import { DataHubSourcesTab } from "@/pages/dataHub/DataHubSourcesTab";
import { DataHubStatusTab } from "@/pages/dataHub/DataHubStatusTab";

const routeApi = getRouteApi("/data-hub");

const styles = {
  description: {
    color: theme.colors.staticFgNote,
    maxWidth: "720px",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: "48px 0px",
  },
  connectPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxWidth: "560px",
    padding: "24px",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    backgroundColor: theme.colors.white,
  },
  connectButton: {
    maxWidth: "240px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const HeaderStatus: React.FC<{
  isConnected: boolean;
  orgs: ReadonlyArray<{ id: number; source: string; name: string }>;
}> = function HeaderStatus({ isConnected, orgs }) {
  const { t } = useTranslation();
  const mergeStateQuery = interlude.unifyScopeSetting.useGetUnifyMergeState(
    undefined,
    { enabled: isConnected },
  );

  if (!isConnected) {
    return (
      <Stack direction="row" gap="8px">
        <Badge variant="plain">
          {t("dataHub.status.notConnected", "Not connected")}
        </Badge>
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap="8px" flexWrap="wrap" alignItems="center">
      <Badge variant="positive">{t("common.connected")}</Badge>
      {mergeStateQuery.isSuccess && !mergeStateQuery.data.isFinished ? (
        <Badge variant="primary">
          {t("dataHub.status.merging", "Merging profiles")}
        </Badge>
      ) : null}
      {orgs.map((org) => (
        // Product codes and org names are data, not copy.
        <Badge key={org.id} variant="plain">
          {`${org.source.toUpperCase()} · ${org.name}`}
        </Badge>
      ))}
    </Stack>
  );
};

const ConnectPanel: React.FC = function ConnectPanel() {
  const { t } = useTranslation();
  return (
    <Box component="section" sx={styles.connectPanel}>
      <Typography variant="h2">
        {t("dataHub.connect.title", "Connect your products to unify profiles")}
      </Typography>
      <Typography variant="body">
        {t(
          "dataHub.connect.description",
          "Profile unification merges the contacts from MAAC and CAAC into one profile per customer, using the identifiers you choose.",
        )}
      </Typography>
      <ExternalLink href={t("applications.cdh.learnMore.link")} trailingIcon>
        {t("common.learnMore")}
      </ExternalLink>
      <Box sx={styles.connectButton}>
        <ProfileUnificationWarning.Provider>
          <ConnectCdhButton />
        </ProfileUnificationWarning.Provider>
      </Box>
    </Box>
  );
};

const tabPanelId = (tab: DataHubTab) => `data-hub-tabpanel-${tab}`;
const tabId = (tab: DataHubTab) => `data-hub-tab-${tab}`;

const ConnectedTabs: React.FC = function ConnectedTabs() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const navigate = useNavigate();
  const tab = search.tab ?? "status";

  const handleChange = useHandler<TabsProps["onChange"]>(
    (_, value: DataHubTab) => {
      navigate({ to: "/data-hub", search: { tab: value } });
    },
  );

  const labels: Record<DataHubTab, string> = {
    status: t("dataHub.tabs.status", "Status"),
    rules: t("dataHub.rules.title", "Merge rules"),
    sources: t("dataHub.sources.title", "Data sources"),
  };

  return (
    <Stack gap="24px">
      <Tabs value={tab} onChange={handleChange}>
        {DATA_HUB_TABS.map((value) => (
          <Tab
            key={value}
            value={value}
            label={labels[value]}
            id={tabId(value)}
            aria-controls={tabPanelId(value)}
          />
        ))}
      </Tabs>
      <Box role="tabpanel" id={tabPanelId(tab)} aria-labelledby={tabId(tab)}>
        {tab === "status" ? <DataHubStatusTab /> : null}
        {tab === "rules" ? <CdhSettings /> : null}
        {tab === "sources" ? <DataHubSourcesTab /> : null}
      </Box>
    </Stack>
  );
};

/**
 * Profile unification: whether the org's products are connected, how
 * profiles are merged, and where customer data comes from. Before connecting,
 * the page shows only the connect step, not empty tabs.
 */
export const DataHubPage: React.FC = function DataHubPage() {
  const { t } = useTranslation();
  const unifyScopeSettingQuery =
    interlude.unifyScopeSetting.useGetUnifyScopeSetting();

  return (
    <PageLayout>
      <Stack gap="8px">
        <PageTitle>{t("dataHub.page.title", "Profile unification")}</PageTitle>
        <Box sx={styles.description}>
          <Typography variant="body">
            {t(
              "dataHub.description",
              "One profile per customer across MAAC, CAAC and your connected channels.",
            )}
          </Typography>
        </Box>
        {unifyScopeSettingQuery.isSuccess ? (
          <HeaderStatus
            isConnected={unifyScopeSettingQuery.data.isCdhConnected}
            orgs={unifyScopeSettingQuery.data.orgs}
          />
        ) : null}
      </Stack>
      {unifyScopeSettingQuery.isPending ? (
        <Box sx={styles.loading}>
          <CircularProgress />
        </Box>
      ) : unifyScopeSettingQuery.isError ? (
        <InfoBox
          variant="error"
          description={unifyScopeSettingQuery.error.message}
        />
      ) : unifyScopeSettingQuery.data.isCdhConnected ? (
        <ConnectedTabs />
      ) : (
        <ConnectPanel />
      )}
    </PageLayout>
  );
};
